import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { TileArgs } from 'react-calendar';
import { Calendar, CalendarProps, Value } from '../../components/Calendar/Calendar';
import { TextInput } from '../../components/Form/TextInput';
import { Icon, close } from '../../components/Icon/Icon';
import { useTranslation } from '../../i18n/i18n';
import './CalendarSelect.css';

interface CalendarSelectProps extends CalendarProps {
	onChangeDate: (value: Value) => void;
	dateValue: string | undefined;
	disabled?: boolean;
	onBlur?: ChangeEventHandler<HTMLDivElement>;
	checkDisabled?: (args: TileArgs) => boolean;
	todayLabel?: string;
	nullable?: boolean;
	minDate?: Date;
	label?: string;
	placeholder?: string;
}

export const CalendarSelect = ({
	onChangeDate,
	minDate,
	maxDate,
	dateValue,
	disabled,
	onBlur,
	checkDisabled,
	todayLabel,
	nullable,
	label,
	placeholder,
}: CalendarSelectProps): JSX.Element => {
	const t = useTranslation().form;
	const [open, setOpen] = useState(false);
	const [touched, setTouched] = useState(false);
	const [changed, setChanged] = useState(false);
	const [position, setPosition] = useState<'bottom' | 'top' | 'hidden'>('hidden');
	const popoverRef = useRef<HTMLDivElement>(null);
	const classes = ['calendar-select', open && 'open', disabled && 'disabled', changed && 'changed']
		.filter(Boolean)
		.join(' ');

	const isElementAboveViewport = (el: HTMLDivElement): boolean =>
		el.getBoundingClientRect().bottom <= (window.innerHeight || document.documentElement.clientHeight);

	useEffect(() => {
		if (popoverRef.current && position === 'hidden') {
			if (isElementAboveViewport(popoverRef.current)) {
				setPosition('bottom');
			} else {
				setPosition('top');
			}
		}

		if (open) popoverRef.current && popoverRef.current.focus();

		if (!open) setPosition('hidden');
	}, [open]);

	const toggleOpen = (): void => setOpen((prev: boolean) => (disabled ? false : !prev));

	const selectDate = (value: Value): void => {
		if (onChangeDate) onChangeDate(value);

		if (value) {
			toggleOpen();
		}
		setChanged(true);
	};

	const renderDateValue = (): string => {
		if (!dateValue) return '';

		if (todayLabel && new Date(dateValue).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)) {
			return todayLabel;
		}

		return dateValue;
	};

	return (
		<>
			<p>{label}</p>
			<div className="calendar-select-container">
				<div
					className={classes}
					onFocus={() => {
						if (!touched) setTouched(true);
						setOpen(true);
					}}
					onBlur={(e) => {
						if (onBlur) onBlur(e);
					}}
				>
					<TextInput
						id={label}
						readOnly={true}
						value={renderDateValue()}
						placeholder={placeholder ? placeholder : t.pickDate}
					/>
					{open && (
						<div
							ref={popoverRef}
							tabIndex={-1}
							className={`popover ${position}`}
							onBlur={(event) => {
								if (!event.currentTarget.contains(event.relatedTarget)) {
									setOpen(false);
								}
							}}
						>
							<Calendar
								checkDisabled={checkDisabled}
								minDate={minDate}
								maxDate={maxDate}
								onChangeDate={selectDate}
								selectedDate={dateValue}
							/>
						</div>
					)}
				</div>
				{nullable && dateValue && (
					<button className="close-btn" onClick={() => selectDate(null)}>
						<Icon shape={{ ...close, fill: 'black' }} size={20} />
					</button>
				)}
			</div>
		</>
	);
};
